import { useState, useCallback } from "react";
import { isAxiosError } from "axios";
import { apiClient } from "@/lib/api";

const getErrorMessage = (err: unknown, fallbackMessage: string): string => {
    if (isAxiosError(err)) {
        return err.response?.data?.message || err.message || fallbackMessage;
    }
    if (err instanceof Error && err.message) {
        return err.message;
    }
    return fallbackMessage;
};

export interface EventTypeData {
    id: number;
    pernikahan_id?: number;
    jenis_acara: string;
    keterangan_acara: string;
    foto_jenis?: string;
    link_demo_acara?: string;
    link_demo_acara2?: string;
    icon?: string;
    article?: string;
    order_list?: number | null;
    created_at?: string | null;
    updated_at?: string | null;
    deleted_at?: string | null;
}

export interface FormDetail {
    id: number;
    form_name: string;
    form_label: string;
    form_type: string; 
    form_value: string; 
    form_placeholder: string;
    urutan?: number; 
}

export interface FormResponseItem {
    id: number;
    id_jenis_acara: number;
    id_acara_list_form: number;
    urutan: number; 
    form_details: FormDetail;
}

export interface ThemeExampleData {
    id: number;
    jenis_acara_id: number;
    template_id: number;
    contoh_pernikahan_id: number;
    image_sampul: string;
    created_at?: string | null;
    updated_at?: string | null;
    template: {
        id: number;
        template_name: string;
        deskripsi: string;
        url_demo: string;
        live_demo: string;
        acara: boolean;
        primary_color: string;
        sec_color: string;
        h_color: string;
        p_color: string;
        o_color: string;
        created_at: string | null;
        updated_at: string | null;
        previews?: Array<{
            id?: number;
            template_id?: number;
            path?: string;
            template_preview_img?: string;
            [key: string]: unknown;
        }> | null;
    } | null;
}

export interface MediaTagItem {
    media_id: number;
    tag: "sampul" | "pria" | "wanita";
}

export interface SubmitMediaTagsPayload {
    media: MediaTagItem[];
}

export const useEventManagement = () => {
    const [eventTypes, setEventTypes] = useState<EventTypeData[]>([]);
    const [formDetails, setFormDetails] = useState<FormDetail[]>([]);
    const [themesList, setThemesList] = useState<ThemeExampleData[]>([]); 
    
    const [isLoadingList, setIsLoadingList] = useState<boolean>(false);
    const [isLoadingForm, setIsLoadingForm] = useState<boolean>(false);
    const [isLoadingCreate, setIsLoadingCreate] = useState<boolean>(false);
    const [isLoadingCheckout, setIsLoadingCheckout] = useState<boolean>(false);
    const [isLoadingThemesList, setIsLoadingThemesList] = useState<boolean>(false); 
    const [isCheckingCoupon, setIsCheckingCoupon] = useState<boolean>(false);
    const [isLoadingSubmitMediaTags, setIsLoadingSubmitMediaTags] = useState<boolean>(false);
    
    const [error, setError] = useState<string | null>(null);

    const fetchEventTypes = useCallback(async () => {
        setIsLoadingList(true);
        setError(null);
        try {
            const response = await apiClient.get("/api/jenis-acara"); 
            const data: EventTypeData[] = response.data?.data || [];

            const sortedEventTypes = [...data].sort((a, b) => {
                const orderA = (a.order_list == null || a.order_list === 0) ? 999 : a.order_list;
                const orderB = (b.order_list == null || b.order_list === 0) ? 999 : b.order_list;

                if (orderA !== orderB) return orderA - orderB;
                return a.id - b.id;
            });

            setEventTypes(sortedEventTypes);
        } catch (err: unknown) {
            setError(getErrorMessage(err, "Gagal mengambil daftar acara"));
        } finally {
            setIsLoadingList(false);
        }
    }, []);

    const fetchFormDetails = useCallback(async (id: string | number) => {
        setIsLoadingForm(true);
        setError(null);
        try {
            const response = await apiClient.get(`/api/jenis-acara/${id}/form`);
            const data: FormResponseItem[] = response.data?.data || [];

            const extractedForms = data.map(item => ({
                ...item.form_details,
                urutan: item.urutan
            }));

            const sortedForms = extractedForms.sort((a, b) => {
                const orderA = (!a.urutan || a.urutan === 0) ? 999 : a.urutan;
                const orderB = (!b.urutan || b.urutan === 0) ? 999 : b.urutan;
                
                return orderA - orderB;
            });

            setFormDetails(sortedForms);
            
        } catch (err: unknown) {
            setError(getErrorMessage(err, "Gagal mengambil detail form"));
        } finally {
            setIsLoadingForm(false);
        }
    }, []);

    const fetchThemesList = useCallback(async (categoryId: number) => {
        setIsLoadingThemesList(true);
        setError(null);
        try {
            const response = await apiClient.get(`/api/list/contoh-undangan?jenis_acara_id=${categoryId}`);
            setThemesList(response.data?.data || []);
        } catch (err: unknown) {
            setError(getErrorMessage(err, "Gagal mengambil daftar tema"));
            setThemesList([]); 
        } finally {
            setIsLoadingThemesList(false);
        }
    }, []);

    const createInvitation = useCallback(async (payload: Record<string, unknown>) => {
        setIsLoadingCreate(true);
        setError(null);
        try {
            const response = await apiClient.post("/api/undangan/create", payload);
            return { success: true, data: response.data };
        } catch (err: unknown) {
            const errMsg = getErrorMessage(err, "Gagal membuat undangan");
            setError(errMsg);
            return { success: false, error: errMsg };
        } finally {
            setIsLoadingCreate(false);
        }
    }, []);

    const submitMediaTags = async (invitationId: string | number, payload: SubmitMediaTagsPayload) => {
        setIsLoadingSubmitMediaTags(true);
        setError(null);
        try {
            const response = await apiClient.post(`/api/undangan/tag/${invitationId}/media`, payload);
            return { success: true, data: response.data };
        } catch (err: unknown) {
            const errMsg = getErrorMessage(err, "Gagal menyimpan tag foto");
            setError(errMsg);
            return { success: false, error: errMsg };
        } finally {
            setIsLoadingSubmitMediaTags(false);
        }
    };

    const checkCoupon = async (code: string, paketId: number | string) => {
        setIsCheckingCoupon(true);
        setError(null);
        try {
            const response = await apiClient.get(`/api/coupon/${code}?paket_id=${paketId}`);
            return { success: true, data: response.data };
        } catch (err: unknown) {
            const errMsg = getErrorMessage(err, "Kupon tidak valid atau gagal diproses");
            setError(errMsg);
            return { success: false, error: errMsg };
        } finally {
            setIsCheckingCoupon(false);
        }
    };

    const checkoutPackage = async (invitationId: string | number, payload: Record<string, unknown>, couponCode?: string) => {
        setIsLoadingCheckout(true);
        setError(null);
        try {
            const endpoint = couponCode 
                ? `/api/payment/${invitationId}?coupon_code=${couponCode}` 
                : `/api/payment/${invitationId}`;
                
            const response = await apiClient.post(endpoint, payload);
            return { success: true, data: response.data };
        } catch (err: unknown) {
            const errMsg = getErrorMessage(err, "Gagal memproses pembayaran");
            setError(errMsg);
            return { success: false, error: errMsg };
        } finally {
            setIsLoadingCheckout(false);
        }
    };

    return {
        eventTypes,
        formDetails,
        themesList,
        checkCoupon,
        fetchEventTypes,
        fetchFormDetails,
        fetchThemesList,
        createInvitation,
        submitMediaTags,
        checkoutPackage, 
        isLoadingList,
        isLoadingForm,
        isLoadingCreate,
        isLoadingCheckout,
        isLoadingThemesList,
        isCheckingCoupon,
        isLoadingSubmitMediaTags,
        error,
    };
};